import { ReactNode, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useUserRole } from '@/hooks/useUserRole';
import { useCoachAssignment } from '@/hooks/useCoachAssignment';
import { useUnreadAudioCount } from '@/hooks/useUnreadAudioCount';
import { useUnreadWritingCount } from '@/hooks/useUnreadWritingCount';
import { useNewGroupSessionCount } from '@/hooks/useNewGroupSessionCount';
import { usePracticeHubAccess } from '@/hooks/usePracticeHubAccess';
import { useLanguageLabAccess } from '@/hooks/useLanguageLabAccess';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

import { MobileBottomNav } from '@/components/MobileBottomNav';
import { NotificationBell } from '@/components/NotificationBell';
import { ChatHeaderIcon } from '@/components/chat/ChatHeaderIcon';
import { BookingDialogProvider, useBookingDialog } from '@/components/booking/BookingDialogProvider';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import philsConversationLogo from '@/assets/phils-conversation-logo.webp';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet';
import { 
  Home, 
  CheckSquare, 
  Mic, 
  BookOpen, 
  Target,
  Calendar,
  ListTodo,
  LogOut,
  Users,
  Menu,
  HelpCircle,
  UsersRound,
  
  User,
  FolderOpen,
  TrendingUp,
  Megaphone,
  CalendarPlus,
  RefreshCw,
  Newspaper,
  FlaskConical,
  Compass,
  ChevronDown,
  Library,
  Headphones,
  Zap,
  Lightbulb,


} from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { SuggestionBoxDialog } from '@/components/shared/SuggestionBoxDialog';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  return (
    <BookingDialogProvider>
      <LayoutInner>{children}</LayoutInner>
    </BookingDialogProvider>
  );
}

function LayoutInner({ children }: LayoutProps) {
  const { user, signOut } = useAuth();
  const { role, isCoach, isConversationPartner, isClient, isAdmin } = useUserRole();
  const { isAssigned } = useCoachAssignment();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const { openBookingDialog } = useBookingDialog();
  const [packageType, setPackageType] = useState<Database['public']['Enums']['package_type'] | null>(null);
  const [fullName, setFullName] = useState<string>('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const unreadAudioCount = useUnreadAudioCount();
  const { unreadCount: unreadWritingCount } = useUnreadWritingCount();
  const coachPracticeBadgeCount = unreadAudioCount + unreadWritingCount;
  const { count: newGroupSessionCount, markGroupSessionsAsSeen } = useNewGroupSessionCount();
  const { enabled: practiceHubEnabled } = usePracticeHubAccess();
  const { enabled: languageLabEnabled } = useLanguageLabAccess();
  // Coach/Admin see Language Lab in the main nav; clients access it via Quick Links when unlocked.
  const canSeeLanguageLabInNav = isCoach || isAdmin;
  // Client "Practise" grouping: Vocabulary, Self Study, Audio
  const practiseRoutes = ['/vocabulary', '/practice', '/audio'];
  const isPractiseActive = practiseRoutes.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/'),
  );
  const clientPractiseBadgeCount = unreadAudioCount;
  // Coach "Tools" grouping: To-Do, Client Tasks, Activity Library, Language Lab
  const isToolsActive = ['/coach-todo', '/tasks', '/activity-library', '/language-lab'].some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/'),
  );


  useEffect(() => {
    const fetchProfileData = async () => {
      if (user) {
        const { data } = await supabase
          .from('profiles')
          .select('package_type, first_name, last_name, full_name, avatar_url')
          .eq('id', user.id)
          .single();
        
        if (data) {
          setPackageType(data.package_type);
          setAvatarUrl(data.avatar_url || null);
          const constructedName = data.first_name && data.last_name 
            ? `${data.first_name} ${data.last_name}`
            : data.full_name || '';
          setFullName(constructedName);
        }
      }
    };

    fetchProfileData();
  }, [user]);

  // Redirect unassigned clients to dashboard from feature pages
  const gatedPaths = ['/tasks', '/sessions', '/practice', '/audio', '/vocabulary', '/goals', '/growth', '/group-booking', '/writing', '/grammar', '/practice-hub', '/book-club', '/language-lab'];
  useEffect(() => {
    if (isClient && !isAssigned && gatedPaths.some(p => location.pathname.startsWith(p))) {
      navigate('/', { replace: true });
    }
  }, [isClient, isAssigned, location.pathname, navigate]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const displayName = fullName || user?.user_metadata?.full_name || 'User';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase() || 'U';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card shadow-soft sticky top-0 z-50 pt-[env(safe-area-inset-top)]">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between lg:gap-8">
            {/* Mobile/Tablet Portrait: Hamburger Menu */}
            <div className="flex items-center lg:hidden">
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" data-tour="menu-button">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 sm:w-80 overflow-y-auto overscroll-contain">
                  <div className="flex items-center gap-3 px-4 py-6 border-b">
                    <Avatar>
                      <AvatarImage src={avatarUrl || undefined} alt={displayName} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <p className="text-sm font-medium">{displayName}</p>
                      {role && (
                        <p className="text-xs text-muted-foreground capitalize">{role}</p>
                      )}
                    </div>
                  </div>
                  <nav className="flex flex-col gap-2 mt-4 pb-[calc(2rem+env(safe-area-inset-bottom))]">
                    <NavLink 
                      to="/" 
                      end
                      onClick={() => setMobileMenuOpen(false)}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                      activeClassName="bg-primary/10 text-primary"
                      data-tour="dashboard"
                    >
                      <Home className="w-4 h-4 inline-block mr-2" />
                      Dashboard
                    </NavLink>

                    {/* Conversation Partner: Show Dashboard and Sessions only */}
                    {isConversationPartner && (
                      <NavLink 
                        to="/sessions"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                        activeClassName="bg-primary/10 text-primary"
                      >
                        <Calendar className="w-4 h-4 inline-block mr-2" />
                        Sessions
                      </NavLink>
                    )}

                    {!isConversationPartner && (
                      <>
                        {isCoach && (
                          <NavLink 
                            to="/admin"
                            onClick={() => setMobileMenuOpen(false)}
                            className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                            activeClassName="bg-primary/10 text-primary"
                          >
                            <Users className="w-4 h-4 inline-block mr-2" />
                            Admin
                          </NavLink>
                        )}
                        
                        <NavLink 
                          to="/sessions"
                          onClick={() => setMobileMenuOpen(false)}
                          className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                          activeClassName="bg-primary/10 text-primary"
                          data-tour="sessions"
                        >
                          <Calendar className="w-4 h-4 inline-block mr-2" />
                          Sessions
                        </NavLink>

                        {!isCoach && (
                          <NavLink 
                            to="/tasks"
                            onClick={() => setMobileMenuOpen(false)}
                            className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                            activeClassName="bg-primary/10 text-primary"
                            data-tour="tasks"
                          >
                            <CheckSquare className="w-4 h-4 inline-block mr-2" />
                            To-Dos
                          </NavLink>
                        )}

                        {!isCoach && (
                          <div className="mt-2">
                            <p className="px-4 pb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">Practise</p>
                            <div className="flex flex-col gap-1 pl-3">
                              <NavLink
                                to="/vocabulary"
                                onClick={() => setMobileMenuOpen(false)}
                                className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                                activeClassName="bg-primary/10 text-primary"
                                data-tour="vocabulary"
                              >
                                <BookOpen className="w-4 h-4 inline-block mr-2" />
                                Vocabulary
                              </NavLink>
                              <NavLink 
                                to="/practice"
                                onClick={() => setMobileMenuOpen(false)}
                                className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                                activeClassName="bg-primary/10 text-primary"
                              >
                                <Target className="w-4 h-4 inline-block mr-2" />
                                Self Study
                              </NavLink>
                              {(packageType === 'active' || packageType === 'plus') && (
                                <NavLink 
                                  to="/audio"
                                  onClick={() => setMobileMenuOpen(false)}
                                  className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex items-center justify-between"
                                  activeClassName="bg-primary/10 text-primary"
                                >
                                  <span>
                                    <Mic className="w-4 h-4 inline-block mr-2" />
                                    Audio
                                  </span>
                                  {unreadAudioCount > 0 && (
                                    <Badge variant="destructive" className="ml-2 h-5 min-w-[20px] flex items-center justify-center">
                                      {unreadAudioCount}
                                    </Badge>
                                  )}
                                </NavLink>
                              )}
                            </div>
                          </div>
                        )}



                        {isCoach && (
                          <NavLink 
                            to="/practice"
                            onClick={() => setMobileMenuOpen(false)}
                            className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex items-center justify-between"
                            activeClassName="bg-primary/10 text-primary"
                            data-tour="vocabulary"
                          >
                            <span>
                              <BookOpen className="w-4 h-4 inline-block mr-2" />
                              Practice
                            </span>
                            {coachPracticeBadgeCount > 0 && (
                              <Badge variant="destructive" className="ml-2 h-5 min-w-[20px] flex items-center justify-center">
                                {coachPracticeBadgeCount}
                              </Badge>
                            )}
                          </NavLink>
                        )}
                        
                        {(isCoach || canSeeLanguageLabInNav) && (
                          <div className="mt-2">
                            <p className="px-4 pb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">Tools</p>
                            <div className="flex flex-col gap-1 pl-3">
                              {isCoach && (
                                <NavLink
                                  to="/coach-todo"
                                  onClick={() => setMobileMenuOpen(false)}
                                  className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                                  activeClassName="bg-primary/10 text-primary"
                                >
                                  <ListTodo className="w-4 h-4 inline-block mr-2" />
                                  To-Do List
                                </NavLink>
                              )}
                              {isCoach && (
                                <NavLink
                                  to="/tasks"
                                  onClick={() => setMobileMenuOpen(false)}
                                  className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                                  activeClassName="bg-primary/10 text-primary"
                                >
                                  <CheckSquare className="w-4 h-4 inline-block mr-2" />
                                  Client Tasks
                                </NavLink>
                              )}
                              {isCoach && (
                                <NavLink
                                  to="/activity-library"
                                  onClick={() => setMobileMenuOpen(false)}
                                  className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                                  activeClassName="bg-primary/10 text-primary"
                                >
                                  <Library className="w-4 h-4 inline-block mr-2" />
                                  Activity Library
                                </NavLink>
                              )}
                              {canSeeLanguageLabInNav && (
                                <NavLink
                                  to="/language-lab"
                                  onClick={() => setMobileMenuOpen(false)}
                                  className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                                  activeClassName="bg-primary/10 text-primary"
                                >
                                  <FlaskConical className="w-4 h-4 inline-block mr-2" />
                                  Language Lab
                                </NavLink>
                              )}
                            </div>
                          </div>
                        )}

                        {!isCoach && (
                          <NavLink 
                            to="/growth"
                            onClick={() => setMobileMenuOpen(false)}
                            className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                            activeClassName="bg-primary/10 text-primary"
                            data-tour="goals"
                          >
                            <TrendingUp className="w-4 h-4 inline-block mr-2" />
                            Growth
                          </NavLink>
                        )}

                        {isCoach && (
                          <NavLink 
                            to="/goals"
                            onClick={() => setMobileMenuOpen(false)}
                            className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                            activeClassName="bg-primary/10 text-primary"
                            data-tour="goals"
                          >
                            <Target className="w-4 h-4 inline-block mr-2" />
                            Growth
                          </NavLink>
                        )}
                      </>
                    )}
                  </nav>

                  {/* Quick Links, same as desktop sidebar */}
                  {isClient && isAssigned && (
                    <div className="mt-4 pt-4 border-t px-4 space-y-4">
                      <div className="space-y-1">
                        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Do</p>
                        <button
                          onClick={() => {
                            setMobileMenuOpen(false);
                            openBookingDialog();
                          }}
                          className="w-full px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex items-center gap-2 text-left"
                        >
                          <CalendarPlus className="w-4 h-4" />
                          Book a session
                        </button>
                        <NavLink
                          to="/group-booking"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            markGroupSessionsAsSeen();
                          }}
                          className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex items-center gap-2"
                          activeClassName="bg-primary/10 text-primary"
                        >
                          <UsersRound className="w-4 h-4" />
                          <span className="flex-1">Group Sessions</span>
                          {newGroupSessionCount > 0 && (
                            <Badge variant="destructive" className="h-5 min-w-[20px] flex items-center justify-center">
                              {newGroupSessionCount}
                            </Badge>
                          )}
                        </NavLink>
                      </div>

                      <div className="space-y-1">
                        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Read</p>
                        <NavLink
                          to="/announcements"
                          onClick={() => setMobileMenuOpen(false)}
                          className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex items-center gap-2"
                          activeClassName="bg-primary/10 text-primary"
                        >
                          <Megaphone className="w-4 h-4" />
                          Phil's Notes
                        </NavLink>
                        <NavLink
                          to="/spotlight-magazine"
                          onClick={() => setMobileMenuOpen(false)}
                          className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex items-center gap-2"
                          activeClassName="bg-primary/10 text-primary"
                        >
                          <Newspaper className="w-4 h-4" />
                          Spotlight Magazine
                        </NavLink>
                        <NavLink
                          to="/book-club"
                          onClick={() => setMobileMenuOpen(false)}
                          className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex items-center gap-2"
                          activeClassName="bg-primary/10 text-primary"
                        >
                          <BookOpen className="w-4 h-4" />
                          Book Club
                        </NavLink>
                      </div>

                      <div className="space-y-1">
                        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Listen & explore</p>
                        <NavLink
                          to="/sparks"
                          onClick={() => setMobileMenuOpen(false)}
                          className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex items-center gap-2"
                          activeClassName="bg-primary/10 text-primary"
                        >
                          <Zap className="w-4 h-4" />
                          Conversation Sparks
                        </NavLink>
                        <NavLink
                          to="/podcast"
                          onClick={() => setMobileMenuOpen(false)}
                          className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex items-center gap-2"
                          activeClassName="bg-primary/10 text-primary"
                        >
                          <Headphones className="w-4 h-4" />
                          Podcast
                        </NavLink>
                        <NavLink
                          to="/practice-hub"
                          onClick={() => setMobileMenuOpen(false)}
                          className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex items-center gap-2"
                          activeClassName="bg-primary/10 text-primary"
                        >
                          <FolderOpen className="w-4 h-4" />
                          Practice Hub
                        </NavLink>
                        {languageLabEnabled && (
                          <NavLink
                            to="/language-lab"
                            onClick={() => setMobileMenuOpen(false)}
                            className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex items-center gap-2"
                            activeClassName="bg-primary/10 text-primary"
                          >
                            <FlaskConical className="w-4 h-4" />
                            Language Lab
                          </NavLink>
                        )}
                      </div>

                      <div className="space-y-1">
                        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Ask</p>
                        <NavLink
                          to="/how-i-practice"
                          onClick={() => setMobileMenuOpen(false)}
                          className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex items-center gap-2"
                          activeClassName="bg-primary/10 text-primary"
                        >
                          <Compass className="w-4 h-4" />
                          How I practise
                        </NavLink>
                        <button
                          onClick={() => {
                            setMobileMenuOpen(false);
                            setSuggestOpen(true);
                          }}
                          className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex items-center gap-2"
                        >
                          <Lightbulb className="w-4 h-4" />
                          Send a suggestion
                        </button>
                      </div>
                    </div>

                  )}

                </SheetContent>
              </Sheet>
            </div>

            {/* Mobile/Tablet Portrait: Centered Logo */}
            <div className="lg:hidden absolute left-1/2 transform -translate-x-1/2">
              <img 
                src={philsConversationLogo} 
                alt="Phil's Conversation" 
                className="h-10 w-auto cursor-pointer"
                onClick={() => navigate('/')}
              />
            </div>

            {/* Desktop & Tablet Landscape: Logo and Nav */}
            <div className="hidden lg:flex items-center gap-8">
              <img 
                src={philsConversationLogo} 
                alt="Phil's Conversation" 
                className="h-10 w-auto cursor-pointer"
                onClick={() => navigate('/')}
              />
              
              <nav className="flex items-center gap-1">
                <NavLink 
                  to="/" 
                  end
                  className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                  activeClassName="bg-primary/10 text-primary"
                  data-tour="dashboard"
                >
                  Dashboard
                </NavLink>

                {/* Conversation Partner: Show Dashboard and Sessions only */}
                {isConversationPartner && (
                  <NavLink 
                    to="/sessions"
                    className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                    activeClassName="bg-primary/10 text-primary"
                  >
                    Sessions
                  </NavLink>
                )}

                {/* Regular users: Full navigation */}
                {!isConversationPartner && (
                  <>
                    {isCoach && (
                      <NavLink 
                        to="/admin"
                        className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                        activeClassName="bg-primary/10 text-primary"
                      >
                        Admin
                      </NavLink>
                    )}
                    
                    <NavLink 
                      to="/sessions"
                      className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                      activeClassName="bg-primary/10 text-primary"
                      data-tour="sessions"
                    >
                      Sessions
                    </NavLink>

                    {!isCoach && (
                      <NavLink 
                        to="/tasks"
                        className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                        activeClassName="bg-primary/10 text-primary"
                        data-tour="tasks"
                      >
                        To-Dos
                      </NavLink>
                    )}

                    {!isCoach && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            data-tour="vocabulary"
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                              isPractiseActive
                                ? 'bg-primary/10 text-primary'
                                : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                            }`}
                          >
                            Practise
                            {clientPractiseBadgeCount > 0 && (
                              <Badge variant="destructive" className="h-5 min-w-[20px] flex items-center justify-center">
                                {clientPractiseBadgeCount}
                              </Badge>
                            )}
                            <ChevronDown className="h-3.5 w-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-48">
                          <DropdownMenuItem onClick={() => navigate('/vocabulary')}>
                            <BookOpen className="mr-2 h-4 w-4" />
                            Vocabulary
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/practice')}>
                            <Target className="mr-2 h-4 w-4" />
                            Self Study
                          </DropdownMenuItem>
                          {(packageType === 'active' || packageType === 'plus') && (
                            <DropdownMenuItem onClick={() => navigate('/audio')}>
                              <Mic className="mr-2 h-4 w-4" />
                              <span className="flex-1">Audio</span>
                              {unreadAudioCount > 0 && (
                                <Badge variant="destructive" className="ml-2 h-5 min-w-[20px] flex items-center justify-center">
                                  {unreadAudioCount}
                                </Badge>
                              )}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}


                    {(isCoach || canSeeLanguageLabInNav) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                              isToolsActive
                                ? 'bg-primary/10 text-primary'
                                : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                            }`}
                          >
                            Tools
                            <ChevronDown className="h-3.5 w-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-48">
                          {isCoach && (
                            <DropdownMenuItem onClick={() => navigate('/coach-todo')}>
                              <ListTodo className="mr-2 h-4 w-4" />
                              To-Do List
                            </DropdownMenuItem>
                          )}
                          {isCoach && (
                            <DropdownMenuItem onClick={() => navigate('/tasks')}>
                              <CheckSquare className="mr-2 h-4 w-4" />
                              Client Tasks
                            </DropdownMenuItem>
                          )}
                          {isCoach && (
                            <DropdownMenuItem onClick={() => navigate('/activity-library')}>
                              <Library className="mr-2 h-4 w-4" />
                              Activity Library
                            </DropdownMenuItem>
                          )}
                          {canSeeLanguageLabInNav && (
                            <DropdownMenuItem onClick={() => navigate('/language-lab')}>
                              <FlaskConical className="mr-2 h-4 w-4" />
                              Language Lab
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}

                    {isCoach && (
                      <NavLink 
                        to="/practice"
                        className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex items-center gap-2"
                        activeClassName="bg-primary/10 text-primary"
                        data-tour="vocabulary"
                      >
                        Practice
                        {coachPracticeBadgeCount > 0 && (
                          <Badge variant="destructive" className="h-5 min-w-[20px] flex items-center justify-center">
                            {coachPracticeBadgeCount}
                          </Badge>
                        )}
                      </NavLink>
                    )}
                    

                    {!isCoach && (
                      <NavLink 
                        to="/growth"
                        className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                        activeClassName="bg-primary/10 text-primary"
                        data-tour="goals"
                      >
                        Growth
                      </NavLink>
                    )}

                    {isCoach && (
                      <NavLink 
                        to="/goals"
                        className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                        activeClassName="bg-primary/10 text-primary"
                        data-tour="goals"
                      >
                        Growth
                      </NavLink>
                    )}
                  </>
                )}
              </nav>
            </div>

            {/* Notification Bell + Chat + Profile Dropdown */}
            <div className="flex items-center gap-1">
            <ChatHeaderIcon />
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-tour="profile">
                  <Avatar>
                    <AvatarImage src={avatarUrl || undefined} alt={displayName} />
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{displayName}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                    {role && (
                      <p className="text-xs text-accent font-medium capitalize">{role}</p>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  Profile Settings
                </DropdownMenuItem>
                {(isAdmin || isCoach) && (
                  <>
                    <DropdownMenuItem
                      onClick={() => {
                        void (async () => {
                          await (window as Window & { __checkForAppUpdate?: () => Promise<void> }).__checkForAppUpdate?.();
                        })();
                      }}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      <span className="flex-1">Check for updates</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={async () => {
                        window.location.reload();
                      }}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      <span className="flex-1">Force refresh app</span>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onClick={() => navigate('/help')}>
                  <HelpCircle className="mr-2 h-4 w-4" />
                  Help
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - add bottom padding for mobile/tablet portrait nav */}
      <main className="container mx-auto px-4 py-6 lg:py-8 pb-20 lg:pb-8">
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav packageType={packageType} avatarUrl={avatarUrl} initials={initials} />

      <SuggestionBoxDialog open={suggestOpen} onOpenChange={setSuggestOpen} />


    </div>
  );
}
